import { useMemo, useState } from 'react'
import { Card, Button } from '@heroui/react'

import { buildBookmarklet } from '../utils/bookmarklet'

import { SystemStatus } from './SystemStatus'

/** HTML-escape a string for safe interpolation into an attribute value. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

interface AdminDashboardProps {
  onNavigate: (
    page:
      | 'algolia'
      | 'logs'
      | 'analytics'
      | 'testing'
      | 'use-cases'
      | 'media-management'
      | 'user-management'
      | 'eval'
      | 'eval-fixtures'
      | 'curation'
  ) => void
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  // Build the bookmarklet against the current origin so it targets prod when
  // viewed on the deployed site and localhost during development.
  const bookmarkletHref = useMemo(
    () =>
      buildBookmarklet(
        typeof window !== 'undefined' ? window.location.origin : ''
      ),
    []
  )

  // The dragged bookmark inherits the anchor text as its name, so the quill
  // emoji (matching the site favicon) becomes its bookmarks-bar icon.
  const bookmarkletAnchor = useMemo(
    () =>
      `<a href="${escapeHtml(
        bookmarkletHref
      )}" class="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white no-underline shadow-sm cursor-grab" onclick="return false">🪶 Add to Cyrano</a>`,
    [bookmarkletHref]
  )

  const [copied, setCopied] = useState(false)
  const copyBookmarklet = async () => {
    await navigator.clipboard.writeText(bookmarkletHref)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* System Status */}
      <SystemStatus />

      {/* Add to Cyrano bookmarklet */}
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="text-4xl">🔖</div>
          <div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Add to Cyrano bookmarklet
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Drag this button to your bookmarks bar, then click it on any
              Reddit page with a GIF. Click a “➕ Cyrano” badge and a small
              popup in the corner adds it automatically — showing progress and
              closing itself on success.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div dangerouslySetInnerHTML={{ __html: bookmarkletAnchor }} />
          <Button size="sm" variant="flat" onPress={copyBookmarklet}>
            {copied ? 'Copied!' : 'Copy bookmarklet code'}
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Want the real Cyrano quill icon instead of the globe? Bookmark this
          page normally (⌘D) so Chrome captures the site icon, then open
          Bookmark Manager, edit that bookmark, replace its URL with the copied
          code, and rename it. Chrome keeps the quill.
        </p>
      </Card>

      {/* Admin Feature Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Curation — the main workflow: add, review, approve, publish */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🎞️</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Curation
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Add GIFs, review what the AI wrote, approve to publish
              </p>
            </div>
          </div>
          <Button
            color="primary"
            variant="flat"
            onPress={() => onNavigate('curation')}
            className="w-full"
          >
            Curate GIFs
          </Button>
        </Card>

        {/* Algolia Management */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🔍</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Algolia Management
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Search index status and reindexing
              </p>
            </div>
          </div>
          <Button
            color="primary"
            variant="flat"
            onPress={() => onNavigate('algolia')}
            className="w-full"
          >
            Manage Search Index
          </Button>
        </Card>

        {/* Recommendation Logs */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">📊</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Recommendation Logs
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Monitor AI recommendation performance
              </p>
            </div>
          </div>
          <Button
            color="secondary"
            variant="flat"
            onPress={() => onNavigate('logs')}
            className="w-full"
          >
            View Logs
          </Button>
        </Card>

        {/* Analytics Dashboard */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">📈</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Analytics Dashboard
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Database insights and text analytics
              </p>
            </div>
          </div>
          <Button
            color="success"
            variant="flat"
            onPress={() => onNavigate('analytics')}
            className="w-full"
          >
            View Analytics
          </Button>
        </Card>

        {/* Recommendation Testing */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🧪</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Recommendation Testing
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Create and run test cases for AI recommendations
              </p>
            </div>
          </div>
          <Button
            color="danger"
            variant="flat"
            onPress={() => onNavigate('testing')}
            className="w-full"
          >
            Manage Tests
          </Button>
        </Card>

        {/* Use Case Analytics */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🎯</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Use Case Analytics
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Analyze GIF use cases and usage patterns
              </p>
            </div>
          </div>
          <Button
            color="primary"
            variant="flat"
            onPress={() => onNavigate('use-cases')}
            className="w-full"
          >
            View Use Cases
          </Button>
        </Card>

        {/* Media Management */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🎬</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Media Management
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Convert GIFs to MP4 for messaging app compatibility
              </p>
            </div>
          </div>
          <Button
            color="success"
            variant="flat"
            onPress={() => onNavigate('media-management')}
            className="w-full"
          >
            Manage Media Files
          </Button>
        </Card>

        {/* User Management */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">👥</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                User Management
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Manage user accounts and activation status
              </p>
            </div>
          </div>
          <Button
            color="warning"
            variant="flat"
            onPress={() => onNavigate('user-management')}
            className="w-full"
          >
            Manage Users
          </Button>
        </Card>

        {/* Eval Fixture Builder */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">⚔️</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Eval Fixture Builder
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Author recommendation eval fixtures — note + traps/forbidden
              </p>
            </div>
          </div>
          <Button
            color="danger"
            variant="flat"
            onPress={() => onNavigate('eval-fixtures')}
            className="w-full"
          >
            Build Fixtures
          </Button>
        </Card>

        {/* Phase 1 Eval */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl">🔬</div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Phase 1 Eval
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Review conversation parsing fixtures and per-model run results
              </p>
            </div>
          </div>
          <Button
            color="secondary"
            variant="flat"
            onPress={() => onNavigate('eval')}
            className="w-full"
          >
            Open Eval Dashboard
          </Button>
        </Card>
      </div>

    </div>
  )
}
